import type { ThemeConfig } from 'antd';

// Ant Design Theme configuration
export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: '#7A9B62',
    colorSuccess: '#4CAF50',
    colorWarning: '#FF9800',
    colorError: '#F44336',
    colorInfo: '#29B6F6',
    colorTextBase: '#212121',
    colorText: '#212121',
    colorTextSecondary: '#616161',
    colorTextDescription: '#757575',
    colorLink: '#7A9B62',
    colorLinkHover: '#5F7A4D',
    colorBgBase: '#FAFBFC',
    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    colorBgLayout: '#F5F5F5',
    fontSize: 14,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    lineHeight: 1.5,
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 4,
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,
    colorBorder: '#E0E0E0',
    colorBorderSecondary: '#EEEEEE',
  },
  components: {
    Button: {
      colorPrimary: '#7A9B62',
      colorPrimaryHover: '#96B37F',
      colorPrimaryActive: '#5F7A4D',
      borderRadius: 8,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,
      paddingInline: 20,
      fontWeight: 500,
    },
    Card: {
      colorBgContainer: '#FFFFFF',
      borderRadiusLG: 12,
      paddingLG: 24,
    },
    Table: {
      borderRadius: 12,
      headerBg: '#F5F5F5',
      headerColor: '#616161',
      rowHoverBg: '#FAFAFA',
      cellPaddingBlock: 16,
      cellPaddingInline: 16,
    },
    Input: {
      borderRadius: 8,
      controlHeight: 44,
      paddingInline: 14,
      colorBorder: '#E0E0E0',
    },
    Select: {
      borderRadius: 8,
      controlHeight: 44,
    },
    Modal: {
      borderRadiusLG: 16,
      paddingContentHorizontal: 24,
      titleFontSize: 20,
    },
    Menu: {
      itemBg: 'transparent',
      itemColor: '#FFFFFF',
      itemHoverBg: 'rgba(255, 255, 255, 0.1)',
      itemHoverColor: '#FFFFFF',
      itemSelectedBg: 'rgba(255, 255, 255, 0.2)',
      itemSelectedColor: '#FFFFFF',
      itemBorderRadius: 8,
    },
    Layout: {
      headerBg: '#5F7A4D',
      siderBg: '#5F7A4D',
      bodyBg: '#FAFBFC',
    },
    Tag: {
      borderRadiusSM: 6,
    },
    Typography: {
      titleMarginBottom: 0,
      titleMarginTop: 0,
    },
  },
};

// Color palette
export const colors = {
  // Primary palette - refined sage green
  primary: '#7A9B62',
  primaryLight: '#96B37F',
  primaryDark: '#5F7A4D',
  primary50: '#F4F7F1',
  primary100: '#E8EFE3',

  // Status colors
  success: '#4CAF50',
  successLight: '#66BB6A',
  successBg: '#E8F5E9',

  warning: '#FF9800',
  warningLight: '#FFB74D',
  warningBg: '#FFF3E0',

  error: '#F44336',
  errorLight: '#EF5350',
  errorBg: '#FFEBEE',

  info: '#29B6F6',
  infoLight: '#4FC3F7',
  infoBg: '#E1F5FE',

  // Text colors
  text: '#212121',
  textSecondary: '#616161',
  textDescription: '#757575',
  textDisabled: '#9E9E9E',
  textLight: '#FFFFFF',

  // Background colors
  background: '#FAFBFC',
  backgroundAlt: '#F5F5F5',
  backgroundSecondary: '#F5F5F5',
  cardBackground: '#FFFFFF',
  surface: '#FFFFFF',

  // Border colors
  border: '#E0E0E0',
  borderLight: '#EEEEEE',

  // Neutral scale
  neutral50: '#FAFAFA',
  neutral100: '#F5F5F5',
  neutral200: '#EEEEEE',
  neutral300: '#E0E0E0',
  neutral400: '#BDBDBD',
  neutral500: '#9E9E9E',
  neutral600: '#757575',
  neutral700: '#616161',
  neutral800: '#424242',
  neutral900: '#212121',
};

// Spacing scale
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
};

// Border radius scale
export const borderRadius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 24,
  full: 9999,
};

// Typography scale
export const typography = {
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
    xl: 20,
    '2xl': 24,
    '3xl': 30,
    '4xl': 36,
    '5xl': 48,
  },
  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// Shadow scale
export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 2px 4px rgba(0, 0, 0, 0.1)',
  lg: '0 4px 8px rgba(0, 0, 0, 0.12)',
  xl: '0 8px 16px rgba(0, 0, 0, 0.15)',
  colored: '0 4px 14px rgba(122, 155, 98, 0.25)',
};

// Note: globalStyles should be imported directly from './globalStyles' to avoid circular dependency
